/**
 * Map helpers: server/Postgres shapes → the field names existing Printer UI components expect.
 */

/** Map a PrintJobs REST/socket row into the dashboard job card shape. */
export function mapJobFromServer(job) {
  if (!job) return null;
  const id = job._id || job.id || job.uuid;
  const created = job._createdDate || job.unix;
  const unix = created instanceof Date ? created : new Date(created || Date.now());

  // Prefer statusRpi for display; fall back to legacy status string.
  let status = job.statusRpi || job.status || "";
  if (status === "in queue") status = "wait";
  if (status === "in progress") status = "printing";
  if (status === "canceled") status = "Cancel";
  if (status === "success") status = "Completed";

  return {
    ...job,
    id,
    uuid: id,
    title: job.title,
    printer_id: job.printerId || job.printer_id,
    room_id: job.roomid || job.room_id,
    roomName: job.roomName || job.roomid_obj?.title || null,
    userName: job.userFullName || job.fileRef_obj?.user_obj?.fullName || null,
    userEmail: job.userEmail || job.fileRef_obj?.user_obj?.email || null,
    status,
    statusRpi: job.statusRpi,
    failReasonTitle: job.failReason_obj?.title || job.failReason?.title || null,
    cancelReasonTitle:
      job.cancelReasonTitle || job.cancelReason_obj?.title || job.cancelReason?.title || null,
    // Only ever set for a CS/dashboard cancel — cancelByUser distinguishes a
    // user-app cancel, which has no admin actor to name here.
    canceledByName: job.canceledByName || job.canceledBy_obj?.fullName || null,
    unix,
    color: job.color,
    duplex: job.duplex,
    copies: job.copies,
    link: job.link,
    // PrintJobs.pages is a snapshot copied from the source PrinterFiles row at
    // creation time — not every job-creation path is guaranteed to have set it,
    // so fall back to the underlying file's own page count when the job's own
    // is missing.
    pages: job.pages ?? job.fileRef_obj?.pages ?? null,
  };
}

/** Map a PrinterStatus REST/socket row into the dashboard printer card shape. */
export function mapPrinterFromServer(row) {
  if (!row) return null;
  // row.printer_id FIRST — the server sets it explicitly as a plain string. row.printer
  // itself is NOT reliable: the server's removeObjKey(..., true) wrapper renames the
  // loaded printer_obj relation (an Items object) onto `printer` whenever `printer` is
  // already truthy, so by the time this reaches the client `row.printer` is the Items
  // object, not the raw id. Using an object as a computed map key coerces to the literal
  // string "[object Object]" for every row, collapsing all printers onto one map key.
  const printerId = row.printer_id || (typeof row.printer === "string" ? row.printer : null) || row.id;
  const status = row.status || "OK";
  return {
    ...row,
    id: printerId,
    printer_id: printerId,
    room_id: row.room_id || row.printer_obj?.room || null,
    room_name: row.room_name || row.printer_obj?.room_obj?.title || row.title || "Printer",
    status: status === "OK" || status === "ok" ? "OK" : status,
    Black: row.tonerBlack ?? row.Black ?? 0,
    Pink: row.tonerPink ?? row.Pink ?? 0,
    Yellow: row.tonerYellow ?? row.Yellow ?? 0,
    Blue: row.tonerBlue ?? row.Blue ?? 0,
    Paper: row.Paper ?? "—",
    "Door status": row["Door status"] ?? "—",
    action: row.action || null,
    title: row.title,
  };
}

export function printersListToMap(list) {
  const map = {};
  (list || []).forEach((row) => {
    const mapped = mapPrinterFromServer(row);
    if (mapped?.printer_id) {
      map[mapped.printer_id] = mapped;
    }
  });
  return map;
}

export function upsertJobInList(jobs, incoming) {
  const mapped = mapJobFromServer(incoming);
  if (!mapped?.id) return jobs || [];
  const list = [...(jobs || [])];
  const idx = list.findIndex((j) => j.id === mapped.id);
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...mapped };
  } else {
    list.unshift(mapped);
  }
  list.sort((a, b) => (b.unix?.getTime?.() || 0) - (a.unix?.getTime?.() || 0));
  return list;
}

export function upsertPrinterInMap(printers, incoming) {
  const mapped = mapPrinterFromServer(incoming);
  if (!mapped?.printer_id) return printers || {};
  return {
    ...(printers || {}),
    [mapped.printer_id]: {
      ...(printers?.[mapped.printer_id] || {}),
      ...mapped,
    },
  };
}
